import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MenuSurface } from '@/components/ui/menu-surface'
import { ABOVE_TAB_BAR } from '@/components/layout/navItems'
import { cn } from '@/lib/utils'

export interface LoanMenuItem {
  label: string
  hint?: string
  danger?: boolean
  onSelect: () => void
}

/** A loan's one main action plus a More menu. Phone: pinned just above the tab bar, within thumb reach. */
export function LoanActionsBar({
  primary,
  items,
}: {
  primary?: { label: string; onClick: () => void }
  items: LoanMenuItem[]
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function close(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  if (!primary && items.length === 0) return null

  return (
    <div
      className={`${ABOVE_TAB_BAR} fixed inset-x-0 z-30 flex gap-2 border-t border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 py-3 sm:static sm:z-auto sm:shrink-0 sm:border-0 sm:bg-transparent sm:p-0`}
    >
      {items.length > 0 && (
        <div ref={menuRef} className="relative flex-1 sm:flex-none">
          <Button
            variant="outline"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="h-auto w-full gap-1 rounded-lg border-[var(--fl-border)] bg-[var(--fl-surface)] px-3.5 py-2.5 text-sm text-[var(--fl-text-1)]"
          >
            More <ChevronDown size={14} />
          </Button>
          {menuOpen && (
            <MenuSurface
              role="menu"
              className="absolute bottom-full left-0 z-40 mb-1.5 w-60 p-1 sm:top-full sm:right-0 sm:bottom-auto sm:left-auto sm:mt-1.5 sm:mb-0"
              onKeyDown={(e) => {
                if (e.key === 'Escape') setMenuOpen(false)
              }}
            >
              {items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    item.onSelect()
                  }}
                  className="block w-full rounded-md px-2.5 py-2 text-left hover:bg-[var(--fl-surface-hover)]"
                >
                  <span
                    className={cn(
                      'block text-sm',
                      item.danger ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
                    )}
                  >
                    {item.label}
                  </span>
                  {item.hint && (
                    <span className="block text-xs text-[var(--fl-text-3)]">{item.hint}</span>
                  )}
                </button>
              ))}
            </MenuSurface>
          )}
        </div>
      )}
      {primary && (
        <Button
          onClick={primary.onClick}
          className="h-auto flex-1 gap-1 rounded-lg px-4 py-2.5 text-sm font-semibold sm:flex-none"
        >
          <Plus size={14} /> {primary.label}
        </Button>
      )}
    </div>
  )
}
